import { useTranslation } from 'react-i18next';

import { getTeamName } from '@/feature/event-details-sbk/utils/get-team-name';
import { ordinal } from '@/utils/numeric/format';
import { getTeamDisplayName } from '@/utils/sport-utils';

import { MarketType, OptionType, Team } from '../types';
import { SET_BETTING_MARKET_CODE, getSetBettingOptionSide } from '../utils/set-betting';

const HIGHEST_SCORING_PERIOD_KEY = 'HIGHEST_SCORING_PERIOD';

const HANDICAP_KEY = 'HANDICAP';

type UseOptionName = {
    optionType: OptionType;
    marketType: MarketType;
    fallback: string;
    homeTeam: Pick<Team, 'short_code' | 'name' | 'alias'>;
    awayTeam: Pick<Team, 'short_code' | 'name' | 'alias'>;
    sportName: string;
    isDisplayFullName?: boolean;
    isShortName?: boolean;
};

type HandicapTranslation = {
    optionType: OptionType;
    marketTypeParams: MarketType['params'];
    fallback: string;
    homeTeam: Pick<Team, 'short_code' | 'name' | 'alias'>;
    awayTeam: Pick<Team, 'short_code' | 'name' | 'alias'>;
    t: ReturnType<typeof useTranslation>['t'];
    isDisplayFullName: boolean;
    isShortName: boolean; // short name is used for to display spread and totals differently, for spread it's just the line "+10" or "-10" (without the team name), for totals its O 35.5 or U 35.5
    sportNameSpace: string;
};

type OptionNameReturner = {
    optionName: string | undefined;
    key: string;
    fallback: string;
};

type OptionTypeParams = MarketType['params'] &
    OptionType['params'] & {
        PERIOD_X_ORDINAL?: string;
        HALF_X_ORDINAL?: string;
        team?: string;
        context?: number;
    };

const getDisplayHandicapLine = (line: number, option: string) => {
    if (line < 0) {
        // Home is favoured
        if (option === 'HOME') {
            return `-${Math.abs(line)}`;
        }
        if (option === 'AWAY') {
            return `+${Math.abs(line)}`;
        }
    } else {
        // Away is favoured
        if (option === 'AWAY') {
            return `-${Math.abs(line)}`;
        }
        if (option === 'HOME') {
            return `+${Math.abs(line)}`;
        }
    }
};

// This function will check if we can find key in translation file, if not it will return fallback
const getOptionNameWithFallback = ({ optionName, key, fallback }: OptionNameReturner) => {
    if (!optionName || optionName === key) {
        return fallback;
    }
    return optionName;
};

// Translation key cascade: sport-specific keys first, then defaults.
// e.g. for Soccer DRAW in RESULT market: soccer.RESULT.DRAW → soccer.DRAW → RESULT.DRAW → DRAW
const getOptionNameKeys = (sportNameSpace: string, marketTypeCode: string, code: string) => [
    `${sportNameSpace}.${marketTypeCode}.${code}`,
    `${sportNameSpace}.${code}`,
    `${marketTypeCode}.${code}`,
    code,
];

const getHandicapTranslation = ({
    marketTypeParams,
    optionType,
    homeTeam,
    awayTeam,
    fallback,
    t,
    isDisplayFullName,
    isShortName,
    sportNameSpace,
}: HandicapTranslation) => {
    if (marketTypeParams.line !== undefined) {
        let line = getDisplayHandicapLine(marketTypeParams.line, optionType.code);
        if (isShortName) {
            // If it's short name, we just return the line without the team name
            return line ?? '';
        }
        const key = `${optionType.code}_HANDICAP`;
        const sportMarketKey = `${sportNameSpace}.${key}`;
        const sportDefaultKey = `${sportNameSpace}.${optionType.code}`;
        const fallbackKey = optionType.code;
        // Tries sport-specific keys first, then falls back to default keys
        // e.g. for Soccer DRAW option: soccer.DRAW_HANDICAP → soccer.DRAW → DRAW_HANDICAP → DRAW
        //@ts-ignore - Passing unknown string to t function
        const optionName = t([sportMarketKey, sportDefaultKey, key, fallbackKey], {
            line,
            homeTeam: isDisplayFullName ? homeTeam.name : homeTeam.short_code,
            awayTeam: isDisplayFullName ? awayTeam.name : awayTeam.short_code,
        }) as string;
        return getOptionNameWithFallback({ optionName, key, fallback });
    }
    return fallback;
};

// checks if UPPER_BOUND and LOWER_BOUND are equal
const isEqualBounds = (params: OptionTypeParams) => {
    return (
        params.UPPER_BOUND !== undefined &&
        params.LOWER_BOUND !== undefined &&
        params.UPPER_BOUND === params.LOWER_BOUND
    );
};

// checks if market type is PLAYER_{{STATISTIC}}_MILESTONE
const isPlayerMilestone = (marketTypeCode: string) => {
    return /^PLAYER_.*_MILESTONE$/.test(marketTypeCode);
};

const isTotalsMarket = (marketTypeCode: string) => {
    return (
        marketTypeCode === 'POINT_OVER_UNDER' ||
        marketTypeCode === 'RUN_OVER_UNDER' ||
        marketTypeCode === 'GOAL_OVER_UNDER'
    );
};

export const useOptionName = ({
    optionType,
    marketType,
    fallback,
    homeTeam,
    awayTeam,
    sportName,
    isDisplayFullName = false,
    isShortName = false,
}: UseOptionName): string => {
    const { t } = useTranslation('sbk_options');
    const sportNameSpace = sportName?.replace(/\s/g, '_').toLowerCase();
    let params: OptionTypeParams = { ...marketType?.params, ...optionType.params };
    let marketTypeCode = marketType.code;
    let code: string = optionType.code;
    if (marketType?.code?.includes(HANDICAP_KEY)) {
        return getHandicapTranslation({
            marketTypeParams: params,
            optionType,
            homeTeam,
            awayTeam,
            fallback,
            t,
            isDisplayFullName,
            isShortName,
            sportNameSpace,
        });
    }
    if (optionType.code === HIGHEST_SCORING_PERIOD_KEY && params.period) {
        const key = `${HIGHEST_SCORING_PERIOD_KEY}_${sportNameSpace.toUpperCase()}`;
        // @ts-ignore - Passing unknown string to t function
        const optionName = t(key, {
            period_ordinal: ordinal(params.period),
        }) as string;
        return getOptionNameWithFallback({ optionName, key, fallback });
    }

    if (marketType.code === SET_BETTING_MARKET_CODE) {
        // Set betting renders one competitor's scoreline. The side is derived from the
        // option's set score (higher score wins) and selects the CORRECT_SCORE_HOME /
        // CORRECT_SCORE_AWAY translation so the score reads winner-first.
        // Early return: the main path turns `params.selections` into an i18next `context`
        // suffix, which would corrupt the score key lookup.
        const side = getSetBettingOptionSide(optionType);
        const scoreCode = side ? `${code}_${side.toUpperCase()}` : code;
        // @ts-ignore - Passing unknown string to t function
        const optionName = t(getOptionNameKeys(sportNameSpace, marketTypeCode, scoreCode), {
            ...optionType.params,
        }) as string;
        const score = getOptionNameWithFallback({ optionName, key: scoreCode, fallback });
        // In the grid (isShortName) the column header already names the competitor, so the
        // bare score is enough. Everywhere else (betslip, bet cards) a lone "2:0" is
        // ambiguous, so prefix the competitor — e.g. "F. Cobolli 2:0" / "N. Djokovic 2:0".
        // Use the same sport-aware label the grid header uses so the names always agree.
        if (isShortName || !side) {
            return score;
        }
        const team = side === 'home' ? homeTeam : awayTeam;
        return `${getTeamDisplayName(team, sportName)} ${score}`;
    }

    if (params.PERIOD_X) {
        params = { ...params, PERIOD_X_ORDINAL: ordinal(params.PERIOD_X) };
    }
    if (params.HALF_X) {
        params = { ...params, HALF_X_ORDINAL: ordinal(params.HALF_X) };
    }
    if (params.TEAM !== undefined) {
        if (params.TEAM === 0) {
            params = { ...params, team: homeTeam.short_code };
        }
        if (params.TEAM === 1) {
            params = { ...params, team: awayTeam.short_code };
        }
    }

    if (isEqualBounds(params)) {
        // Appending EQUAL to option code if UPPER_BOUND and LOWER_BOUND are equal to adjust the translation
        // from being "CHW by 1-1" to "CHW by 1"
        code = `${code}_EQUAL`;
    }

    if (params.selections) {
        // using the `context` option here to allow for translations with the same key but different number of selections
        // https://www.i18next.com/translation-function/context
        params = { ...params, context: params.selections };
    }

    if (isPlayerMilestone(marketType.code)) {
        marketTypeCode = 'player_milestone';
    }

    // @ts-ignore - Passing unknown string to t function
    let optionName = t(getOptionNameKeys(sportNameSpace, marketTypeCode, code), {
        ...params,
        homeTeam: isDisplayFullName ? getTeamName(homeTeam) : homeTeam.short_code,
        awayTeam: isDisplayFullName ? getTeamName(awayTeam) : awayTeam.short_code,
        fallback: code,
    }) as string;

    if (isTotalsMarket(marketTypeCode) && isShortName) {
        const optionNameArray = optionName.split(' ');
        if (optionNameArray.length === 2) {
            optionName = `${optionNameArray[0][0]} ${optionNameArray[1]}`;
        }
    }
    return getOptionNameWithFallback({ optionName, key: code, fallback });
};
